import React from "react";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import styled, { CSSObject } from "@emotion/styled";
import { useDebouncedState } from "@react-hookz/web";
import { palette, space } from "@stedi/dls/constants";
import { CopyToClipboardButton } from "@stedi/dls/CopyToClipboardButton";
import { Flex } from "@stedi/dls/Flex";
import { Logo } from "@stedi/dls/Logo";
import { stediJsonata } from "@stedi/transformers-jsonata";
import { useIsMounted } from "../hooks/useIsMounted";
import { Orientation, usePanelOrientation } from "../hooks/usePanelOrientation";
import { isParentNotStediDomain } from "../lib/parentDomain";
import { usePlaygroundURLState } from "../lib/state";
import { EditInPlaygroundLink } from "./EditInPlaygroundLink";
import { ExpressionEditor, LazyExpressionEditor } from "./Editor/ExpressionEditor";
import { OutputEditor } from "./Editor/OutputEditor";
import { LazySourceEditor, SourceEditor } from "./Editor/SourceEditor";
import "react-reflex/styles.css";

const SReflexContainer = styled(ReflexContainer)({
  "&.reflex-container": {
    backgroundColor: palette.gray6,
    position: "relative",
    flex: 1,
    height: "unset",
    width: "unset",

    "> .reflex-splitter": {
      background: "none !important",
      backgroundClip: "content-box !important",
      border: "none !important",
      transition: "none !important",
      position: "relative",
      zIndex: 1,
    },

    "&.vertical > .reflex-splitter": {
      width: space.s2 + 1,
      marginLeft: -space.s1,
      marginRight: -space.s1,

      "&:after": {
        content: '""',
        position: "absolute",
        marginLeft: space.s1,
        width: 1,
        height: "100%",
        backgroundColor: palette.gray2,
      },

      "&:hover, &:active": {
        "&:after": {
          marginLeft: space.s1 - 1,
          width: 3,
        },
      },
    },

    "&.horizontal > .reflex-splitter": {
      height: space.s2 + 1,
      marginTop: -space.s1,
      marginBottom: -space.s1,

      "&:after": {
        content: '""',
        position: "absolute",
        marginTop: space.s1,
        height: 1,
        width: "100%",
        backgroundColor: palette.gray2,
      },

      "&:hover, &:active": {
        "&:after": {
          marginTop: space.s1 - 1,
          height: 3,
        },
      },
    },
  },
});

const SPlaygroundPanel = styled(ReflexElement)(
  ({ orientation, resizable, size }: { orientation?: Orientation; resizable?: boolean; size?: number }) =>
    ({
      minWidth: 0,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      transition: "background 80ms ease-in-out",

      flex: !resizable && size !== undefined ? "none !important" : "unset",
      width: !resizable && orientation === "vertical" ? `${size}px !important` : "unset",
      height: !resizable && orientation === "horizontal" ? `${size}px !important` : "unset",

      ":not(:first-of-type)": {
        borderLeft: !resizable && orientation === "vertical" ? `1px solid ${palette.gray2}` : "none",
        borderTop: !resizable && orientation === "horizontal" ? `1px solid ${palette.gray2}` : "none",
      },
    }) as CSSObject,
);

const SEditablePlaygroundPanel = styled(SPlaygroundPanel)({
  "&:focus-within": {
    backgroundColor: palette.gray4,
  },
});

const SPlaygroundPanelTitleBar = styled(Flex)({
  paddingLeft: space.s3 + space.s2,
  paddingRight: space.s3 + space.s2,
  paddingTop: space.s3,
  paddingBottom: space.s2,
  justifyContent: "space-between",
});

const SPlaygroundPanelTitle = styled.h2({
  color: palette.gray14,
});

const SDimmedPlaygroundPanelTitle = styled.h2({
  color: palette.gray11,
});

const SPlaygroundPanelTitleDetails = styled.span({
  color: palette.gray11,
});

const SPlaygroundPanelControls = styled(Flex)({
  gap: space.s2 + space.s1,
});

const SPlaygroundPanelContent = styled(Flex)({
  flex: 1,
  position: "relative",
  minHeight: 0,
});

const SEditorWrapper = styled.div({
  position: "absolute",
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
});

const SBranding = styled(Flex)({
  position: "absolute",
  bottom: space.s3,
  right: space.s3 + space.s2,
  paddingTop: space.s2,
  paddingLeft: space.s2,
  backgroundColor: palette.gray6,
});

const SLogoLink = styled.a({
  color: palette.green,
});

interface PlaygroundProps {
  defaultSource: string | undefined;
  defaultExpression: string | undefined;
  isResizable: boolean;
  sourcePanelHeight?: number;
  expressionPanelHeight?: number;
  isLazy: boolean;
  outputPanelHeight?: number;
  isEmbedded: boolean;
}

function Playground({
  defaultExpression,
  defaultSource,
  expressionPanelHeight,
  isEmbedded,
  isLazy,
  isResizable,
  outputPanelHeight,
  sourcePanelHeight,
}: PlaygroundProps) {
  const [URLState, setURLState] = usePlaygroundURLState({
    defaultExpression,
    defaultSource,
  });

  const handleOnSourceChange = async (newSourceValue: string | undefined) => {
    await setURLState((currentURLState) => ({ ...currentURLState, source: newSourceValue }));
  };

  const handleOnExpressionChange = async (newExpressionValue: string | undefined) => {
    await setURLState((currentURLState) => ({
      ...currentURLState,
      expression: newExpressionValue,
    }));
  };

  const { expression, source } = URLState ?? {};
  const debouncedOutputValue = useDebouncedStediJsonataOutput({ expression, source });

  const orientation = usePanelOrientation();

  const isMounted = useIsMounted();
  const shouldShowBranding = isEmbedded && isParentNotStediDomain(isMounted);

  const ExpressionEditorToRender = isLazy ? LazyExpressionEditor : ExpressionEditor;
  const SourceEditorToRender = isLazy ? LazySourceEditor : SourceEditor;

  return (
    <SReflexContainer orientation={orientation}>
      <SEditablePlaygroundPanel minSize={sourcePanelHeight ? undefined : MIN_PANEL_SIZE_PX} size={sourcePanelHeight}>
        <SPlaygroundPanelTitleBar>
          <SPlaygroundPanelTitle>Source</SPlaygroundPanelTitle>
          <SPlaygroundPanelControls>
            {isEmbedded ? <EditInPlaygroundLink /> : null}
            <CopyToClipboardButton copyMessage="Copy source to clipboard" value={source ?? ""} />
          </SPlaygroundPanelControls>
        </SPlaygroundPanelTitleBar>
        <SPlaygroundPanelContent>
          <SEditorWrapper>
            <SourceEditorToRender defaultValue={source} onChange={handleOnSourceChange} />
          </SEditorWrapper>
        </SPlaygroundPanelContent>
      </SEditablePlaygroundPanel>

      {isResizable ? <ReflexSplitter propagate={true} /> : null}

      <SEditablePlaygroundPanel
        minSize={expressionPanelHeight ? undefined : MIN_PANEL_SIZE_PX}
        size={expressionPanelHeight}
      >
        <SPlaygroundPanelTitleBar>
          <Flex gap="s2">
            <SPlaygroundPanelTitle>JSONata</SPlaygroundPanelTitle>
            <SPlaygroundPanelTitleDetails>v2.0.1</SPlaygroundPanelTitleDetails>
          </Flex>
          <SPlaygroundPanelControls>
            <CopyToClipboardButton copyMessage="Copy JSONata to clipboard" value={expression ?? ""} />
          </SPlaygroundPanelControls>
        </SPlaygroundPanelTitleBar>
        <SPlaygroundPanelContent>
          <SEditorWrapper>
            <ExpressionEditorToRender
              defaultValue={expression}
              error={debouncedOutputValue.error}
              onChange={handleOnExpressionChange}
              onMount={(editorInstance) => {
                if (!isEmbedded) {
                  editorInstance.focus();
                }
              }}
              source={source}
            />
          </SEditorWrapper>
        </SPlaygroundPanelContent>
      </SEditablePlaygroundPanel>

      {isResizable ? <ReflexSplitter propagate={true} /> : null}

      <SPlaygroundPanel minSize={outputPanelHeight ? undefined : MIN_PANEL_SIZE_PX} size={outputPanelHeight}>
        <SPlaygroundPanelTitleBar>
          <SDimmedPlaygroundPanelTitle>Output</SDimmedPlaygroundPanelTitle>
          <SPlaygroundPanelControls>
            <CopyToClipboardButton copyMessage="Copy output to clipboard" value={debouncedOutputValue.result ?? ""} />
          </SPlaygroundPanelControls>
        </SPlaygroundPanelTitleBar>
        <SPlaygroundPanelContent>
          <SEditorWrapper>
            <OutputEditor error={debouncedOutputValue.error} value={debouncedOutputValue.result} />
          </SEditorWrapper>
        </SPlaygroundPanelContent>
      </SPlaygroundPanel>

      {shouldShowBranding ? (
        <SBranding>
          <SLogoLink href="https://stedi.com" target="_blank">
            <Logo fill={palette.green} width={40} />
          </SLogoLink>
        </SBranding>
      ) : null}
    </SReflexContainer>
  );
}

export { Playground };

const MIN_PANEL_SIZE_PX = 200;

interface UseDebouncedStediJsonataProps {
  expression: string | undefined;
  source: string | undefined;
}

const useDebouncedStediJsonataOutput = ({ expression, source }: UseDebouncedStediJsonataProps) => {
  const [debouncedState, setDebouncedState] = useDebouncedState(
    () => {
      return getExpressionOutput({ expression, source });
    },
    300,
    0,
  );

  React.useEffect(() => {
    setDebouncedState(getExpressionOutput({ expression, source }));
  }, [expression, setDebouncedState, source]);

  return debouncedState;
};

interface GetExpressionOutputProps {
  expression: string | undefined;
  source: string | undefined;
}

interface ExpressionOutput {
  result: string;
  error: Error | undefined;
}

const emptyExpressionOutput: ExpressionOutput = {
  result: "",
  error: undefined,
};

export const getExpressionOutput = ({ expression, source }: GetExpressionOutputProps): ExpressionOutput => {
  try {
    if (expression === undefined || expression.trim() === "") {
      return emptyExpressionOutput;
    }

    const sourceObject = source ? JSON.parse(source) : {};
    const output = stediJsonata(expression).evaluate(sourceObject);

    if (output === undefined) {
      return emptyExpressionOutput;
    }

    return { result: JSON.stringify(output, null, 2), error: undefined };
  } catch (e) {
    return { result: "", error: e as Error };
  }
};
